import {
    NODE_TYPE_MAP,
    RESOURCE_TYPE_MAP,
    FUNCTION_CODE_MAP,
    DEFAULT_VIEWPORT,
} from '../constants/canvas';
import { FUNCTION_LABELS } from '../components/AddNodePopover';
import {
    toSerializableData,
    sanitizeIdentifier,
    getSlotParamKey,
    isMultiImageFunctionKey,
} from './common';
import { getNodeSize } from './graph';

export const buildNodeRenderData = (node, canvasViewport = DEFAULT_VIEWPORT) => {
    const { width, height } = getNodeSize(node);

    return toSerializableData({
        type: node.type,
        position: node.position,
        style: {
            ...node.style,
            ...(node.type === 'groupNode' ? { width, height } : {}),
        },
        width,
        height,
        parentId: node.parentId,
        extent: node.extent,
        expandParent: node.expandParent,
        data: {
            label: node.data?.label,
            imageSequence: node.data?.imageSequence,
            imageSequenceLabel: node.data?.imageSequenceLabel,
            src: node.data?.src,
            functionKey: node.data?.functionKey,
            displayMode: node.data?.displayMode,
            selectedOutputId: node.data?.selectedOutputId,
            executionStatus: node.data?.executionStatus,
            groupExecutionLocked: !!node.data?.groupExecutionLocked,
            activeExecutionId: node.data?.activeExecutionId || '',
            inputParams: node.data?.inputParams,
            panelState: node.data?.panelState,
            latestExecution: node.data?.latestExecution || null,
        },
        canvasViewport,
    });
};

export const buildEdgeRenderData = (edge) => toSerializableData({
    type: edge.type,
    inputSlotKey: edge.data?.inputSlotKey,
});

export const buildCreativeImg2ImgBindings = (node, edges) => {
    const targetEdges = edges.filter((edge) => edge.target === node.id);
    if (targetEdges.length === 0) return [];

    const edgeMap = new Map(targetEdges.map((edge) => [String(edge.id), edge]));
    // 优先从 panelState.uploadItems 中提取 linked 项的顺序，
    // 保证 paramBindings 与 imageUrls 中 source:"line" 项的顺序一致。
    const uploadItems = Array.isArray(node.data?.panelState?.uploadItems)
        ? node.data.panelState.uploadItems
        : [];
    const linkedUploads = Array.isArray(node.data?.panelState?.linkedUploads)
        ? node.data.panelState.linkedUploads
        : [];
    const orderedBindings = [];
    const usedEdgeIds = new Set();

    const tryAddBinding = (edgeId) => {
        if (!edgeId) return;
        const edgeKey = String(edgeId);
        if (usedEdgeIds.has(edgeKey)) return;
        const matchedEdge = edgeMap.get(edgeKey);
        if (!matchedEdge) return;
        usedEdgeIds.add(edgeKey);
        orderedBindings.push({
            paramKey: 'imageUrls',
            sourceNodeId: sanitizeIdentifier(matchedEdge.source),
            sourceOutputKey: '',
        });
    };

    // 按 uploadItems 中 linked 项顺序添加 binding
    uploadItems.forEach((item) => {
        if (item?.type === 'linked') tryAddBinding(item.edgeId);
    });

    // 如果 uploadItems 为空，倒退到原来的 linkedUploads 顺序
    if (orderedBindings.length === 0) {
        linkedUploads.forEach((item) => tryAddBinding(item?.edgeId));
    }

    // 将不在以上源中的 edges 追加到末尾（兆底）
    targetEdges.forEach((edge) => tryAddBinding(edge.id));

    return orderedBindings.filter((binding) => binding.sourceNodeId);
};

export const buildNodePayload = (node, edges, canvasViewport = DEFAULT_VIEWPORT) => {
    const nodeType = NODE_TYPE_MAP[node.type];
    const functionKey = node.data?.functionKey;
    const skipSelectValue = node.data?.panelState?.skipSelect ?? node.data?.inputParams?.skipSelect;
    const manual = !skipSelectValue;
    const resolveBindingParamKey = (edge) => (
        getSlotParamKey(functionKey, edge.data?.inputSlotKey)
        || (isMultiImageFunctionKey(functionKey) ? 'imageUrls' : '')
    );
    const incomingBindings = nodeType === 'function'
        ? (
            isMultiImageFunctionKey(functionKey)
                ? buildCreativeImg2ImgBindings(node, edges)
                : edges
                    .filter((edge) => edge.target === node.id)
                    .map((edge) => ({
                        paramKey: resolveBindingParamKey(edge),
                        sourceNodeId: sanitizeIdentifier(edge.source),
                    }))
                    .filter((binding) => binding.paramKey && binding.sourceNodeId)
                    .sort((a, b) => (
                        a.paramKey.localeCompare(b.paramKey) || a.sourceNodeId.localeCompare(b.sourceNodeId)
                    ))
        )
        : [];

    const nodeName = node.data?.imageSequenceLabel || node.data?.label || '';

    return {
        id: sanitizeIdentifier(node.id),
        nodeName,
        manual,
        nodeType,
        parentId: node.parentId ? sanitizeIdentifier(node.parentId) : undefined,
        resourceType: RESOURCE_TYPE_MAP[node.type],
        functionCode: nodeType === 'function'
            ? (FUNCTION_CODE_MAP[functionKey] || functionKey || '')
            : undefined,
        functionName: nodeType === 'function'
            ? (node.data?.label || FUNCTION_LABELS[functionKey] || '')
            : undefined,
        inputParams: nodeType === 'function' || nodeType === 'resource'
            ? toSerializableData(node.data?.inputParams || {})
            : undefined,
        paramBindings: nodeType === 'function' ? incomingBindings : undefined,
        groupConfig: nodeType === 'group' ? {} : undefined,
        renderData: buildNodeRenderData(node, canvasViewport),
    };
};

export const buildEdgePayload = (edge) => ({
    id: sanitizeIdentifier(edge.id),
    sourceNodeId: sanitizeIdentifier(edge.source),
    targetNodeId: sanitizeIdentifier(edge.target),
    renderData: buildEdgeRenderData(edge),
});

// 对比前后画布数据，生成后端批量更新指令
export const buildCanvasBatchOps = ({
    beforeNodes = [],
    beforeEdges = [],
    afterNodes = [],
    afterEdges = [],
    beforeViewport = DEFAULT_VIEWPORT,
    afterViewport = DEFAULT_VIEWPORT,
}) => {
    const beforeNodeMap = new Map(beforeNodes.map((node) => {
        const payload = buildNodePayload(node, beforeEdges, beforeViewport);
        return [payload.id, payload];
    }));
    const afterNodeMap = new Map(afterNodes.map((node) => {
        const payload = buildNodePayload(node, afterEdges, afterViewport);
        return [payload.id, payload];
    }));
    const beforeEdgeMap = new Map(beforeEdges.map((edge) => {
        const payload = buildEdgePayload(edge);
        return [payload.id, payload];
    }));
    const afterEdgeMap = new Map(afterEdges.map((edge) => {
        const payload = buildEdgePayload(edge);
        return [payload.id, payload];
    }));
    const nodeOps = [];
    const edgeOps = [];

    beforeNodeMap.forEach((node) => {
        if (!afterNodeMap.has(node.id)) {
            nodeOps.push({ op: 'delete', node });
        }
    });

    afterNodeMap.forEach((node) => {
        const prevNode = beforeNodeMap.get(node.id);
        if (!prevNode) {
            nodeOps.push({ op: 'add', node });
            return;
        }

        if (JSON.stringify(prevNode) !== JSON.stringify(node)) {
            nodeOps.push({ op: 'update', node });
        }
    });

    beforeEdgeMap.forEach((edge) => {
        if (!afterEdgeMap.has(edge.id)) {
            edgeOps.push({ op: 'delete', edge });
        }
    });

    afterEdgeMap.forEach((edge) => {
        const prevEdge = beforeEdgeMap.get(edge.id);
        if (!prevEdge) {
            edgeOps.push({ op: 'add', edge });
            return;
        }

        if (JSON.stringify(prevEdge) !== JSON.stringify(edge)) {
            edgeOps.push({ op: 'update', edge });
        }
    });

    return { nodeOps, edgeOps };
};